import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptionalAuth } from '@/contexts/AuthContext';

export interface MyCompany {
  companyId: string;
  companyName: string;
  logoUrl: string | null;
  role: 'manager' | 'coordinator' | 'employee';
}

export function useMyCompanies(userIdOverride?: string | null) {
  const auth = useOptionalAuth();
  const user = userIdOverride !== undefined ? (userIdOverride ? { id: userIdOverride } : null) : auth?.user ?? null;

  const query = useQuery({
    queryKey: ['my-companies', user?.id],
    queryFn: async (): Promise<MyCompany[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('company_members')
        .select('company_id, role, care_companies(id, name, logo_url)')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (error || !data) return [];

      return data
        .map((m: any) => ({
          companyId: m.company_id,
          companyName: m.care_companies?.name ?? '',
          logoUrl: m.care_companies?.logo_url ?? null,
          role: m.role as MyCompany['role'],
        }))
        .filter((c) => !!c.companyName);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    companies: query.data ?? [],
    isLoading: query.isLoading,
  };
}
